import type { Router } from 'vue-router'
import NProgress from 'nprogress'
import { useUserStore } from '@/store/user'
import { usePermissionStore } from '@/store/permission'
import { useTabsStore } from '@/store/tabs'
import { APP_TITLE } from '@/settings'

/**
 * 路由守卫 —— 对应旧工程的「登录态校验 + 权限校验」逻辑。
 *
 * 旧工程把这两件事分散在：
 *   - 每个 html 里 sessionStorage 判断
 *   - common.js 的 globalAjaxError 处理 300/305/308
 * 新工程统一收敛到守卫 + 请求拦截器，逻辑更集中。
 */

const WHITE_LIST = ['/login', '/systemMaintenance']

export function setupRouterGuard(router: Router) {
  router.beforeEach(async (to, _from, next) => {
    NProgress.start()
    const userStore = useUserStore()

    // 未登录
    if (!userStore.isLogged) {
      if (WHITE_LIST.includes(to.path) || to.meta?.public) {
        return next()
      }
      return next({ path: '/login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : undefined })
    }

    // 已登录再访问登录页 -> 回首页
    if (to.path === '/login') {
      return next('/index')
    }

    // 首次进入加载菜单（后端菜单表无需改动，见 permission store 的地址转换）
    const permissionStore = usePermissionStore()
    if (!permissionStore.loaded) {
      try {
        await permissionStore.loadMenu()
      } catch {
        /* 菜单加载失败不阻断进入，页面内可重试 */
      }
    }

    next()
  })

  router.afterEach((to) => {
    const userStore = useUserStore()
    const tabsStore = useTabsStore()
    if (userStore.isLogged && !to.meta?.hideTab) {
      tabsStore.add(to)
    }
    document.title = to.meta?.title ? `${to.meta.title} - ${APP_TITLE}` : APP_TITLE
    NProgress.done()
  })

  router.onError(() => {
    NProgress.done()
  })
}
